/**
 * Review helpers for a schedule upload (the planner's grid or row list read
 * into one job order per batch): the names worth a look, fixing a name once
 * for every row that carries it, and the overlaps the server would refuse.
 * Pure — no React, no server.
 */
import { AvailableProduct, ExtractedWorkOrder } from "../../../../services/production-plan-api";
import { WorkOrder } from "./calculations";

/** A line on one plant's plan. */
export interface PlantLine {
	name: string;
	locationId: number;
	locationName: string;
}

export interface PlantLineIndex {
	/** What the file's lines are matched against: the line name, or "name · plant" when two plants share the name. */
	labels: string[];
	byLabel: Map<string, PlantLine>;
}

/**
 * One upload can carry every plant's plan (Granules plans 3701 and 3725
 * together): match against all the plants' lines and save each order to the
 * plant its line belongs to. A name two plants share is told apart by plant.
 */
export function indexPlantLines(lines: PlantLine[]): PlantLineIndex {
	const counts = new Map<string, number>();
	lines.forEach((line) => counts.set(line.name, (counts.get(line.name) ?? 0) + 1));
	const byLabel = new Map<string, PlantLine>();
	lines.forEach((line) => {
		const label = (counts.get(line.name) ?? 0) > 1 ? `${line.name} · ${line.locationName}` : line.name;
		byLabel.set(label, line);
	});
	return { labels: Array.from(byLabel.keys()).sort((a, b) => a.localeCompare(b)), byLabel };
}

/** Attribute key the requirement note ("Dec Req · 1M") is kept under. */
export const NOTES_ATTR_KEY = "notes";

export interface NameToCheck<T> {
	raw: string;
	/** What the reader matched it to (null = nothing). */
	current: T | null;
	/** exact, name, spelling, ai or null. */
	by: string | null;
	count: number;
}

export interface NamesToCheck {
	lines: NameToCheck<string>[];
	products: NameToCheck<number>[];
}

/** A person's answer for a name: a line / product id, or null to leave those rows out. */
export interface NameFixes {
	lines: Record<string, string | null>;
	products: Record<string, number | null>;
}

const SURE = new Set(["exact", "name"]);

/** Lines and products the reader was not sure of, each once with how many rows carry it. */
export function namesToCheck(orders: ExtractedWorkOrder[]): NamesToCheck {
	const lines = new Map<string, NameToCheck<string>>();
	const products = new Map<string, NameToCheck<number>>();
	orders.forEach((order) => {
		const lineKey = order.rawWorkstation || "";
		if (lineKey && !SURE.has(order.workstationMatchedBy ?? "")) {
			const seen = lines.get(lineKey);
			if (seen) seen.count++;
			else lines.set(lineKey, { raw: lineKey, current: order.workstation || null, by: order.workstationMatchedBy ?? null, count: 1 });
		}
		const productKey = order.rawProduct || "";
		if (!order.orderType && productKey && !SURE.has(order.productMatchedBy ?? "")) {
			const seen = products.get(productKey);
			if (seen) seen.count++;
			else products.set(productKey, { raw: productKey, current: order.productId ?? null, by: order.productMatchedBy ?? null, count: 1 });
		}
	});
	const byCount = <T>(a: NameToCheck<T>, b: NameToCheck<T>) => b.count - a.count || a.raw.localeCompare(b.raw);
	return { lines: Array.from(lines.values()).sort(byCount), products: Array.from(products.values()).sort(byCount) };
}

/** The rows with every fixed name applied and their status worked out again. */
export function applyNameFixes(orders: ExtractedWorkOrder[], fixes: NameFixes, products: AvailableProduct[]): ExtractedWorkOrder[] {
	const byId = new Map(products.map((product) => [product.id, product]));
	return orders.map((order) => {
		const next: ExtractedWorkOrder = { ...order, validationErrors: [] };
		if (order.rawWorkstation && order.rawWorkstation in fixes.lines) {
			next.workstation = fixes.lines[order.rawWorkstation] ?? "";
		}
		if (!order.orderType && order.rawProduct && order.rawProduct in fixes.products) {
			const id = fixes.products[order.rawProduct];
			const product = id ? byId.get(id) : undefined;
			next.productId = product?.id;
			next.productName = product?.name ?? "";
		}
		if (!next.workstation) next.validationErrors.push(`Line not matched: ${order.rawWorkstation}`);
		if (!next.orderType && !next.productId) next.validationErrors.push(`Product not matched: ${order.rawProduct}`);
		if (!next.startTime) next.validationErrors.push("Start time is required");
		if (!next.quantity || next.quantity <= 0) next.validationErrors.push("Valid quantity is required");
		next.isValid = next.validationErrors.length === 0;
		return next;
	});
}

interface Window {
	/** Line, and plant when known: two plants can each have a "Dispensing". */
	workstation: string;
	start: number;
	end: number;
}

const windowOf = (workstation: string, startTime: string, endTime?: string | null, locationId?: number | null): Window => {
	const start = new Date(startTime).getTime();
	const end = endTime ? new Date(endTime).getTime() : start;
	return { workstation: locationId ? `${locationId}|${workstation}` : workstation, start, end: Math.max(end, start) };
};

const overlaps = (a: Window, b: Window) => a.workstation === b.workstation && a.start < b.end && b.start < a.end;

export interface UploadConflicts {
	/** Orders already on the plan that an uploaded one overlaps on the same line. */
	existing: WorkOrder[];
	/** Uploaded rows (by index) that overlap one already planned. */
	overlappingUploaded: number[];
	/** Uploaded rows (by index) that overlap an earlier uploaded row on the same line; they are left out. */
	selfOverlapping: number[];
}

/** What the server would refuse: one order per line at a time. */
export function findConflicts(
	uploaded: (ExtractedWorkOrder & { locationId?: number | null })[],
	planned: WorkOrder[]
): UploadConflicts {
	// Compare by plant too only when both sides know it.
	const byPlant = uploaded.every((order) => order.locationId) && planned.every((order) => order.locationId);
	const plannedWindows = planned
		.filter((order) => order.workstation)
		.map((order) => ({ order, window: windowOf(order.workstation, order.startTime, order.endTime, byPlant ? order.locationId : null) }));
	const existing = new Map<number, WorkOrder>();
	const overlappingUploaded: number[] = [];
	const selfOverlapping: number[] = [];
	const kept: Window[] = [];
	uploaded
		.map((order, index) => ({ index, window: windowOf(order.workstation, order.startTime, order.endTime, byPlant ? order.locationId : null) }))
		.sort((a, b) => a.window.start - b.window.start)
		.forEach(({ index, window }) => {
			if (kept.some((other) => overlaps(other, window))) {
				selfOverlapping.push(index);
				return;
			}
			kept.push(window);
			const hits = plannedWindows.filter((p) => overlaps(p.window, window));
			if (hits.length > 0) {
				overlappingUploaded.push(index);
				hits.forEach((hit) => existing.set(hit.order.id, hit.order));
			}
		});
	return { existing: Array.from(existing.values()), overlappingUploaded, selfOverlapping };
}

/** The note as the job order's attribute values (merged with any it already has). */
export function attributeValuesWithNote(note?: string | null, current?: string | null): string | undefined {
	if (!note) return current ?? undefined;
	let values: Record<string, string> = {};
	try {
		values = current ? JSON.parse(current) : {};
	} catch {
		values = {};
	}
	return JSON.stringify({ ...values, [NOTES_ATTR_KEY]: note });
}
